/**
 * Per-country DPA / regulatory block. Shown at the bottom of every legal
 * section panel — same data, every panel, scrolls with the section.
 *
 * `legalInfo` shape mirrors `Country.legalInfo` JSON in Prisma. Any null/
 * undefined field is silently skipped — countries early in the rollout
 * may have only a partial DPA record.
 */

import type { LegalTranslator } from "./sections";
import { tLocal } from "./i18n";

export interface LegalInfo {
  dpaFullName?: Record<string, string>;
  dpaUrl?: string;
  dpaAddress?: string;
  localDataLaw?: string;
  consumerLaw?: Record<string, string>;
  consumerAuthority?: Record<string, string>;
  healthDataLaw?: string;
  minAge?: number;
  additionalNotes?: Record<string, string>;
}

interface DpaBlockProps {
  t: LegalTranslator;
  legalInfo: LegalInfo;
  /** Display name of the country in the active locale */
  countryName: string;
  /** Active locale (for resolving LocalizedString fields on legalInfo) */
  locale: string;
}

export function DpaBlock({ t, legalInfo, countryName, locale }: DpaBlockProps) {
  const titleText = t("countrySection.title", { country: countryName });

  return (
    <div className="bg-muted/50 border-border not-prose mt-10 rounded-xl border p-5 sm:p-6">
      <h3 className="text-foreground mb-4 text-base font-semibold">{titleText}</h3>

      <dl className="space-y-3 text-sm">
        {legalInfo.dpaFullName && (
          <div>
            <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t("countrySection.dpa")}
            </dt>
            <dd className="text-foreground mt-0.5">
              {tLocal(legalInfo.dpaFullName, locale)}
              {legalInfo.dpaUrl && (
                <>
                  {" — "}
                  <a
                    href={legalInfo.dpaUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {legalInfo.dpaUrl.replace(/^https?:\/\//, "")}
                  </a>
                </>
              )}
            </dd>
            {legalInfo.dpaAddress && (
              <dd className="text-muted-foreground mt-0.5 text-xs">{legalInfo.dpaAddress}</dd>
            )}
          </div>
        )}

        {legalInfo.localDataLaw && (
          <div>
            <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t("countrySection.dataLaw")}
            </dt>
            <dd className="text-foreground mt-0.5">{legalInfo.localDataLaw}</dd>
          </div>
        )}

        {legalInfo.consumerLaw && (
          <div>
            <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t("countrySection.consumerProtection")}
            </dt>
            <dd className="text-foreground mt-0.5">{tLocal(legalInfo.consumerLaw, locale)}</dd>
            {legalInfo.consumerAuthority && (
              <dd className="text-muted-foreground mt-0.5 text-xs">
                {tLocal(legalInfo.consumerAuthority, locale)}
              </dd>
            )}
          </div>
        )}

        {legalInfo.healthDataLaw && (
          <div>
            <dt className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
              {t("countrySection.healthLaw")}
            </dt>
            <dd className="text-foreground mt-0.5">{legalInfo.healthDataLaw}</dd>
          </div>
        )}

        {legalInfo.additionalNotes && (
          <div className="border-border border-t pt-3">
            <p className="text-muted-foreground text-xs italic">
              {tLocal(legalInfo.additionalNotes, locale)}
            </p>
          </div>
        )}
      </dl>
    </div>
  );
}
